import Component from "../../lib/component";
import {ModalboxConfig} from "../modalbox/config";
import {XHR_ACCEPT_HEADERS, XHR_REQUESTED_WITH_HEADERS} from "../../lib/xhr_fetcher";
import {getGlobalComponentOfType} from "../../lib/global_components";
import {getRankersUserAgent} from "../../constants/rankers_user_agent";

export const ModalizerConfig = {
    componentTypeKey: 'Modalizer',
    baseHtmlClass: 'modalizer',
    selector: '.modalizer',

    datasetConfigKey: 'modalizer__config',
    datasetConfigUrlKey: 'url',

    formModalizerType: 'FORM',

    getHTTPMethod: 'GET',
    headHTTPMethod: 'HEAD',
    putHTTPMethod: 'PUT',
    postHTTPMethod: 'POST',
};

export class Modalizer extends Component {

    static get config() {
        return ModalizerConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: ModalizerConfig
        });

        this.setDatasetConfig(ModalizerConfig.datasetConfigKey);
        this.url = this.getUrl();

        this.element.addEventListener((this.isFormModalizerType() ? 'submit' : 'click'), this.open.bind(this));

        this.render();
    }

    open(event) {
        event.preventDefault();
        const modalbox = this.getModalbox();
        if (modalbox) modalbox.open({calling_el: this.element});
        this.requestContent();
    }

    requestContent() {
        if (!this.url) {
            console.warn('[Modalizer] Url could not be found on element:', this.element);
            return;
        }
        let request = this.getRequest(this.url);
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    // Return both the response and a flag indicating it's not ok
                    return { isNotOk: true, response };
                }
                return { isNotOk: false, response };
            })
            .then(result => {
                if (result.isNotOk) {
                    return result.response.text().then(content => {
                        let errorContent = getErrorContent(content);
                        const modalbox = this.getModalbox();
                        if (modalbox) modalbox.update(errorContent);
                        // You may want to throw an error after updating the modal
                        throw new Error('[Modalizer] Network response was not OK');
                    });
                } else {
                    return result.response.text();
                }
            })
            .then(this.handleResponse.bind(this))
            .catch((error) => {
                console.error('[Modalizer] There has been a problem with your fetch operation:', error);
            });
    }

    getRequest(url) {
        let options = {};
        options.method = this.getHTTPMethod();

        // When there are params we need to include them in the request.
        // For GET/HEAD requests they are appended to the URL.
        // Otherwise they are included in the request body.
        let params = this.getFormParams();
        const hasParams = (params) ? params.entries().next().value !== undefined : false;
        if (hasParams) {
            if (options.method === ModalizerConfig.getHTTPMethod || options.method === ModalizerConfig.headHTTPMethod) {
                for (const pair of params) {
                    url.searchParams.append(pair[0], pair[1])
                }
            } else {
                options.body = params;
            }
        }

        // Include cookies in the request to approved domains (based on the cookie's `domain` value).
        // The server also needs to respond to the preflight OPTIONS request with the `Access-Control-Allow-Credentials` header set to `true`.
        // See `CrossDomainAccess#set_cross_domain_access` in the Rails app where this is set.
        options.credentials ??= 'include';

        let request = new Request(url, options);

        // Note that with CORS the server must allow these headers to be set.
        // See the Rails controller module `CrossDomainAccess#set_cross_domain_access`.
        request.headers.set('Accept', XHR_ACCEPT_HEADERS); // Important! Ensures that the Rails `format.js` response block is run.
        request.headers.set('X-Requested-With', XHR_REQUESTED_WITH_HEADERS); // Important! Ensures that the Rails method `request.xhr?` returns true.

        // Set the Rankers user agent header. This is used to specify the client that is making the request.
        // Setting the header (rather than relying on the client's user agent) is necessary because the user agent is not always correctly set when making JS requests from a webview.
        // This is a failsafe so that the server can still identify the client even if the user agent is not set correctly.
        request.headers.set('X-Rankers-User-Agent', getRankersUserAgent());

        return request;
    }

    handleResponse(content) {
        const modalbox = this.getModalbox();
        if (modalbox) modalbox.update(content, this.url?.toString());
    }

    isFormModalizerType() {
        return (this.element.nodeName.toUpperCase() === ModalizerConfig.formModalizerType);
    }

    getUrl() {
        let url = null;
        try {
            url = new URL(this.getUrlString());
        } catch (TypeError) { // Generally because the `url` is a relative url and a URL instance needs a base set.
            url = new URL(this.getUrlString(), `${window.location.protocol}//${window.location.host}`);
        }
        return url;
    }

    getUrlString() {
        const configUrl = this.getDatasetConfigValue(Modalizer.config.datasetConfigUrlKey);
        if (configUrl) return configUrl;
        if (this.element.hasAttribute('action')) return this.element.getAttribute('action');
        if (this.element.hasAttribute('href')) return this.element.getAttribute('href');
        return null;
    }

    getFormParams() {
        return this.isFormModalizerType() ? new FormData(this.element) : new FormData();
    }

    getHTTPMethod() {
        return this.element.hasAttribute('method') ? this.element.getAttribute('method').toUpperCase() : ModalizerConfig.getHTTPMethod;
    }

    getModalbox() {
        return getGlobalComponentOfType(ModalboxConfig.componentTypeKey);
    }
}

function getErrorContent(htmlText) {
    const match = htmlText.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return match ? `<div style="padding:2em;">${match[1]}</div>` : `<div style="padding:2em;">Sorry, an error has occurred!</div>`;
}
